import { gql } from '@apollo/client'
import type { ObservableQuery } from '@apollo/client'
import React from 'react'

import { setSearchParams } from 'appUtils'
import {
  addParamChangeListener,
  encodeFilterMap,
  encodeNSearch,
  encodeSortMap,
  getSearchParamProps,
  getTotalPageCnt,
  removeParamChangeListener,
  setFilter,
  setInFilter,
} from 'utils'

import { PaginatedResultObjBase } from './PaginatedResultObjBase'
import { MemTableObj, getMemTableObj } from './MemTableObj'
import { DetailHandler, EditFormHandler } from './handlers'
import { generateDetailUI, generateEditUI } from './uiBuilders'
import { getNodeData, generateGQLString } from './ast'
import { getUIAdapter, ngqlInfo } from './state'
import { getFirstError, getOrderStrByName, processSorter } from './ngqlUtils'
import type {
  BaseUIHandler,
  FilterMap,
  FormInstance,
  GQLApolloClient,
  GQLObjOptions,
  GQLResponseData,
  GQLResponseNode,
  GQLVariables,
  GraphQLErrorLike,
  NineNodeBase,
  NineOperation,
  NodeDataSet,
  ReactObj,
  ResultUIData,
  SearchPreset,
} from './types'

export class ResultObj extends PaginatedResultObjBase {
  queryObj: NineNodeBase
  declare gqlopt: GQLObjOptions
  declare reactObj: Record<string, unknown> | null
  uiInfo: Record<string, unknown>
  nodeList: NineNodeBase[]

  // Form/UI 연동 필드
  detailUI?: DetailHandler | null
  editUI?: EditFormHandler | null
  validate?: (key?: string) => Promise<void>
  validateAll?: () => Promise<void>
  getFields?: (key?: string, exceptReadOnly?: boolean) => Record<string, unknown>
  setFields?: (values: Record<string, unknown>, key?: string) => void
  needDataClear?: boolean

  // GQL 상태 (과거 GQLObj 팩토리의 클로저)
  private queryText: string
  private useUI: boolean
  private fixedVariables: Record<string, unknown>
  private searchFilters: Record<string, unknown> = {}
  private refetchPrevVariables: GQLVariables | null = null

  // ResultObj 고유 상태 (공통 상태는 PaginatedResultObjBase)
  isParamCheckDisabled: boolean = false
  searchPreset: SearchPreset | null = null

  // Apollo 핸들
  observableQuery: ObservableQuery<unknown> | null = null
  subscription: { unsubscribe: () => void } | null = null
  observable: {
    subscribe: (observer: Record<string, unknown>) => { unsubscribe: () => void }
    resetLastResults?: () => void
  } | null = null

  constructor(queryObj: NineNodeBase, gqlopt: GQLObjOptions, reactObj: ReactObj | undefined) {
    super(reactObj as unknown as Record<string, unknown>, gqlopt as Record<string, unknown>)

    this.queryObj = queryObj
    this.gqlopt = gqlopt || {}
    this.queryText = generateGQLString(queryObj as NineOperation)
    this.useUI = gqlopt == null || gqlopt.useUI !== false
    this.variables = gqlopt != null && gqlopt.vars != null ? (gqlopt.vars as GQLVariables) : {}
    this.fixedVariables =
      gqlopt != null && gqlopt.fixedVars != null
        ? (gqlopt.fixedVars as Record<string, unknown>)
        : {}

    this.uiInfo = {}

    if (this.gqlopt.uiInfo != null) {
      this.uiInfo = { ...this.gqlopt.uiInfo }
    }

    if (this.reactObj != null) {
      this.setUIObj?.({
        dataList: [],
        resData: {},
      })
    }

    this.nodeList = []

    Object.keys(queryObj.fields || {}).forEach((key) => {
      if (
        queryObj != null &&
        queryObj.fields != null &&
        queryObj.fields[key] != null &&
        (queryObj.fields[key] as NineNodeBase).fields != null
      ) {
        const { fields, isArray } = queryObj.fields[key] as NineNodeBase
        if (fields!.edges != null) {
          const { node } = (fields!.edges as NineNodeBase).fields!
          this.nodeList.push(node as NineNodeBase)
        } else if (isArray === true) {
          this.nodeList.push(queryObj.fields[key] as NineNodeBase)
        } else {
          this.nodeList.push(queryObj.fields[key] as NineNodeBase)
        }
      }
    })

    if (gqlopt != null && gqlopt.searchItems != null) {
      this.setSearchItems(gqlopt.searchItems as Record<string, unknown>)
    }
  }

  getUIObj(): ResultUIData {
    return this.dataObj as ResultUIData
  }

  setUIInfo = (options: { loadingStateName?: string; orders?: string[] }): void => {
    if (options.loadingStateName != null) {
      this.setLoadingName?.(options.loadingStateName)
    }

    if (options.orders != null) {
      this.uiInfo.orders = options.orders
      ;(this.uiInfo.orders as string[]).forEach((key: string) => {
        if (
          this.queryObj != null &&
          this.queryObj.fields != null &&
          this.queryObj.fields[key] != null &&
          (this.queryObj.fields[key] as NineNodeBase).fields != null
        ) {
          const { fields, isArray } = this.queryObj.fields[key] as NineNodeBase
          if (fields!.edges != null) {
            const { node } = (fields!.edges as NineNodeBase).fields!
            this.nodeList.push(node as NineNodeBase)
          } else if (isArray === true) {
            this.nodeList.push(fields as unknown as NineNodeBase)
          } else {
            this.nodeList.push(fields as unknown as NineNodeBase)
          }
        }
      })
    }
  }

  mappingResponse = (resParent: Record<string, unknown>): void => {
    if (this.needDataClear !== false) {
      this.setUIObj?.({ dataList: [], resData: {} }, false)
    }

    const { dataList, resData } = (this.getUIObj?.() as ResultUIData) || {
      dataList: [],
      resData: {},
    }

    if (this.uiInfo.orders == null) {
      Object.keys(resParent).forEach((key) => {
        const data = getNodeData(resParent, key, this.arrayIFace as never) as NodeDataSet | null

        if (data != null) {
          if (resData[key] == null || this.needDataClear !== false) {
            dataList.push(data)
            resData[key] = data
          } else {
            resData[key].nodes = resData[key].nodes.concat(data.nodes)
            resData[key].totalCount = data.totalCount
            resData[key].hasNext = data.hasNext
          }
        }
      })
    } else {
      ;(this.uiInfo.orders as string[]).forEach((key: string) => {
        const data = getNodeData(resParent, key, this.arrayIFace as never) as NodeDataSet | null

        if (data != null) {
          if (resData[key] == null || this.needDataClear !== false) {
            dataList.push(data)
            resData[key] = data
          } else {
            resData[key].nodes = resData[key].nodes.concat(data.nodes)
            resData[key].totalCount = data.totalCount
            resData[key].hasNext = data.hasNext
          }
        }
      })
    }

    this.setUIObj?.({ dataList, resData })
  }

  private getSubValue = (
    key: string,
    value: Record<string, unknown>,
  ): Record<string, unknown> | null => {
    let data: Record<string, unknown> | null = null

    Object.keys(value).some((subKey) => {
      if (subKey === 'ok' || subKey === 'errors') {
        return false
      }
      const subValue = value[subKey]
      data = {
        name: key,
        isEdges: true,
        type: 'edges',
      }

      if (Array.isArray(subValue) === true) {
        data.nodes = subValue
      } else {
        data.nodes = [subValue]
      }

      // edges 인 것처럼 만들어 줌.
      data.nodes = (data.nodes as GQLResponseNode[]).map((item: unknown) => {
        return { node: item, cursor: null }
      })
      data.totalCount = (data.nodes as GQLResponseNode[]).length
      return true
    })

    return data
  }

  mappingMutationResponse = (resParent: Record<string, unknown>): void => {
    this.setUIObj?.({ dataList: [], resData: {} }, false)

    const { dataList, resData } = (this.getUIObj?.() as ResultUIData) || {
      dataList: [],
      resData: {},
    }

    if (this.uiInfo.orders == null) {
      Object.keys(resParent).forEach((key) => {
        const data = this.getSubValue(key, resParent[key] as Record<string, unknown>) as
          | NodeDataSet
          | undefined
        if (data != null) {
          dataList.push(data)
          resData[key] = data
        }
      })
    } else {
      ;(this.uiInfo.orders as string[]).forEach((key: string) => {
        const data = this.getSubValue(key, resParent[key] as Record<string, unknown>) as
          | NodeDataSet
          | undefined
        if (data != null) {
          dataList.push(data)
          resData[key] = data
        }
      })
    }

    this.setUIObj?.({ dataList, resData })
  }

  getResult = (): NodeDataSet[] => {
    const { dataList } = (this.getUIObj?.() as ResultUIData) || { dataList: [] }
    return dataList
  }

  getResultMap = (): Record<string, NodeDataSet> => {
    const { resData } = (this.getUIObj?.() as ResultUIData) || { resData: {} }
    return resData
  }

  getResultData = (key?: string): NodeDataSet | undefined => {
    const { dataList, resData } = (this.getUIObj?.() as ResultUIData) || {
      dataList: [],
      resData: {},
    }
    if (key != null) {
      return resData[key]
    }
    return dataList[0]
  }

  getResultNodes = (key?: string): GQLResponseNode[] => {
    const { dataList, resData } = (this.getUIObj?.() as ResultUIData) || {
      dataList: [],
      resData: {},
    }
    if (key != null) {
      return resData[key].nodes
    }
    return dataList[0].nodes
  }

  clear = (key?: string): void => {
    const { dataList, resData } = (this.getUIObj?.() as ResultUIData) || {
      dataList: [],
      resData: {},
    }
    if (key != null) {
      if (resData[key] != null) {
        resData[key].nodes = []
        resData[key].totalCount = 0
      }
    } else if (dataList[0] != null) {
      dataList[0].nodes = []
      dataList[0].totalCount = 0
    }

    this.setAllDataList?.(dataList, false)
  }

  getResultNode = (key?: string): Record<string, unknown> => {
    const { dataList, resData } = (this.getUIObj?.() as ResultUIData) || {
      dataList: [],
      resData: {},
    }
    if (dataList.length > 0) {
      const nodes = key != null ? resData[key].nodes : dataList[0].nodes
      if (nodes.length > 0) {
        return nodes[0].node as Record<string, unknown>
      }
    }
    return {}
  }

  /**
   * NodeConnection 구조의 깊은 재귀 접근. 각 레벨의 `node` 는 BaseUIHandler 이며
   * 트리 생성 경로(generateEditUI/generateDetailUI)가 자식 타입 일관성을 보장한다.
   */
  private resolveNodeUIObj = (nodeData: { node: BaseUIHandler }): BaseUIHandler => {
    const deepNode = (nodeData.node.nodeInfo.map.edges as { node: BaseUIHandler }).node
    return (deepNode.nodeInfo.map.node as { node: BaseUIHandler }).node
  }

  private attachGetAllElem = (ret: BaseUIHandler): void => {
    ret.getAllElem = (uiAttrs: unknown) => {
      const adapter = getUIAdapter()
      return adapter.createAllElem(
        ret as unknown as Parameters<typeof adapter.createAllElem>[0],
        uiAttrs,
      )
    }
  }

  private attachTableFieldNodes = (ret: BaseUIHandler): void => {
    ret.getTableFieldNodes = (key: string) => {
      if (key == null) {
        return null
      }
      const nodeData = ret.nodeInfo.map[key]
      if (nodeData == null || nodeData.isTable !== true) {
        return null
      }
      return this.resolveNodeUIObj(nodeData).getNodes()
    }
  }

  private attachTableObj = (
    ret: BaseUIHandler,
    data: Record<string, unknown> | null,
    selfObj: ResultObj,
  ): void => {
    ret.getTableObj = (key: string, tableOpts: unknown) => {
      if (key == null) {
        return null
      }

      if (ret.nodeInfo.memTableMap[key] == null) {
        const nodeInfo = ret.nodeInfo.map[key]
        if (nodeInfo == null || nodeInfo.isTable !== true) {
          return null
        }
        ret.nodeInfo.memTableMap[key] = getMemTableObj(
          [],
          tableOpts as GQLObjOptions,
          selfObj.reactObj as unknown as ReactObj,
        )
      }

      if (data != null && data[key] != null) {
        const memTable = ret.nodeInfo.memTableMap[key] as MemTableObj
        memTable.setData(((data[key] as Record<string, unknown>).edges as unknown[]) || [])
      }

      return ret.nodeInfo.memTableMap[key]
    }
  }

  /** DetailHandler 에 공통 렌더 헬퍼 주입 (isEdit 분기 없음). */
  private applyDetailHelpers = (
    ret: DetailHandler,
    data: Record<string, unknown> | null,
    selfObj: ResultObj,
  ): void => {
    this.attachGetAllElem(ret)
    this.attachTableFieldNodes(ret)
    this.attachTableObj(ret, data, selfObj)

    ret.getTableFieldElem = (key: string, uiAttrs: unknown, _btnsUI: unknown) => {
      if (key == null) {
        return null
      }
      const nodeData = ret.nodeInfo.map[key]
      if (nodeData == null || nodeData.isTable !== true) {
        return null
      }
      const uiObj = this.resolveNodeUIObj(nodeData)
      const adapter = getUIAdapter()
      return (adapter.createDetailContainer as (...args: unknown[]) => React.ReactElement)(
        uiObj,
        ret.nodeInfo.options,
        nodeData.uiProps,
        uiAttrs,
      )
    }
  }

  /** EditFormHandler 에 공통 렌더 헬퍼 주입 (createEditContainer + formRef 연결). */
  private applyEditHelpers = (
    ret: EditFormHandler,
    data: Record<string, unknown> | null,
    selfObj: ResultObj,
  ): void => {
    this.attachGetAllElem(ret)
    this.attachTableFieldNodes(ret)
    this.attachTableObj(ret, data, selfObj)

    ret.getTableFieldElem = (key: string, uiAttrs: unknown, btnsUI: unknown) => {
      if (key == null) {
        return null
      }
      const nodeData = ret.nodeInfo.map[key]
      if (nodeData == null || nodeData.isTable !== true) {
        return null
      }
      const uiObj = this.resolveNodeUIObj(nodeData)
      const formRef = ret.getFormRef!(key)
      const adapter = getUIAdapter()
      return (adapter.createEditContainer as (...args: unknown[]) => React.ReactElement | null)(
        uiObj,
        ret.nodeInfo.options,
        nodeData.uiProps,
        uiAttrs,
        formRef,
        btnsUI,
      )
    }
  }

  private getSubNodeObj = (
    nodeInfo: { key: string; idx?: number },
    nodeObj: { node: NineNodeBase; data: Record<string, unknown> | null },
  ): { node: NineNodeBase; data: Record<string, unknown> | null } => {
    const ret = nodeObj.node.fields![nodeInfo.key] as NineNodeBase
    let subValues: unknown = null
    if (nodeObj.data != null) {
      subValues = nodeObj.data[nodeInfo.key]
      if ((ret as NineNodeBase).isArray === true && subValues != null) {
        const idx = nodeInfo.idx || 0
        subValues = (subValues as unknown[])[idx]
      }
    }
    return {
      node: ret,
      data: subValues as Record<string, unknown> | null,
    }
  }

  // nodeInfoArr = [{key: fieldKey, idx: edge인 경우 사용.}, ...]
  getSubDetailUI = (
    nodeInfoArr: Array<{ key: string; idx?: number }>,
    options?: Record<string, unknown>,
  ): DetailHandler => {
    if (this.nodeList == null || this.nodeList.length === 0) {
      throw new Error('Field must not be empty.')
    }

    let nodeObj = {
      node: this.nodeList[0],
      data: this.getResultNode(),
    }

    nodeInfoArr.forEach((nodeInfo) => {
      nodeObj = this.getSubNodeObj(nodeInfo, nodeObj)
    })

    const { node, data } = nodeObj
    const opts = options || {}

    if (node.uiProps != null) {
      opts.uiNodeProps = node.uiProps
    }

    const ret = generateDetailUI(
      node.fields,
      opts as never,
      data,
      this.reactObj as unknown as ReactObj,
    )

    return ret
  }

  getDetailUI = (options?: Record<string, unknown>): DetailHandler => {
    if (this.nodeList == null || this.nodeList.length === 0) {
      throw new Error('Field must not be empty.')
    }

    const opts = options || {}

    const { dataList } = (this.getUIObj?.() as ResultUIData) || { dataList: [] }

    const data =
      dataList != null && dataList.length > 0 && dataList[0].nodes.length > 0
        ? (dataList[0].nodes[0].node as Record<string, unknown>)
        : null
    const node = this.nodeList[0]

    if (node.uiProps != null) {
      opts.uiNodeProps = node.uiProps
    }

    if (this.loadingStateInfo) {
      this.loadingStateInfo.enabled = false
    }

    const ret = generateDetailUI(
      node.fields,
      opts as never,
      data,
      this.reactObj as unknown as ReactObj,
    )

    this.detailUI = ret

    ret.name = this.name

    ret.getChildNodes = (key?: string) => {
      const innerNode = key != null ? ret.nodeInfo.map[key].node : ret
      return innerNode.getNodes()
    }

    ret.getElem = (key?: string, uiAttrs?: unknown) => {
      const innerNode = key != null ? ret.nodeInfo.map[key].node : ret
      const qNode = key != null ? node.fields![key] : node
      const { uiProps } = qNode as NineNodeBase

      if (Object.keys(innerNode.compList).length === 0) {
        return null
      }

      const adapter = getUIAdapter()
      return (adapter.createDetailContainer as (...args: unknown[]) => React.ReactElement)(
        innerNode,
        options,
        uiProps,
        uiAttrs,
      )
    }

    this.applyDetailHelpers(ret, data, this)

    return ret
  }

  // nodeInfoArr = [{key: fieldKey, idx: edge인 경우 사용.}, ...]
  getSubEditUI = (
    nodeInfoArr: Array<{ key: string; idx?: number }>,
    options?: Record<string, unknown>,
  ): EditFormHandler => {
    if (this.nodeList == null || this.nodeList.length === 0) {
      throw new Error('Field must not be empty.')
    }

    let nodeObj = {
      node: this.nodeList[0],
      data: this.getResultNode(),
    }

    nodeInfoArr.forEach((nodeInfo) => {
      nodeObj = this.getSubNodeObj(nodeInfo, nodeObj)
    })

    const { node, data } = nodeObj
    const opts = options || {}

    if (node.uiProps != null) {
      opts.uiNodeProps = node.uiProps
    }

    const ret = generateEditUI(
      node.fields,
      opts as never,
      data,
      this.reactObj as unknown as ReactObj,
    )

    this.validate = async (key?: string) => {
      if (key == null || key === ret.name) {
        await ret.validateForm!()
      } else {
        await ret.validateSubForm!(key)
      }
    }

    this.validateAll = async () => {
      await ret.validateForm!()

      if (ret.nodeInfo.list.length > 0) {
        Object.keys(ret.nodeInfo.map).forEach(async (key) => {
          if (ret.nodeInfo.map[key].isTable !== true) {
            await ret.validateSubForm!(key)
          }
        })
      }
    }

    this.getFields = (key?: string, exceptReadOnly?: boolean) => {
      if (key == null || key === ret.name) {
        return ret.getFormFields!(exceptReadOnly)
      }
      return ret.getSubFormFields!(key, exceptReadOnly)
    }

    this.setFields = (values: Record<string, unknown>, key?: string) => {
      if (key == null || key === ret.name) {
        return ret.setFormFields!(values)
      }
      return ret.setSubFormFields!(key, values)
    }

    ret.getFormRef = (key?: string) => {
      const formKey = key == null ? this.name : key
      if (ret.refMap == null) {
        ret.refMap = {}
      }
      if (ret.refMap[formKey] == null) {
        ret.refMap[formKey] = React.createRef()
      }
      return ret.refMap[formKey]
    }

    return ret
  }

  getEditUI = (options?: Record<string, unknown>): EditFormHandler => {
    if (this.nodeList == null || this.nodeList.length === 0) {
      throw new Error('Field must not be empty.')
    }

    const opts = options || {}

    const { dataList } = (this.getUIObj?.() as ResultUIData) || { dataList: [] }

    const data =
      dataList != null && dataList.length > 0
        ? (dataList[0].nodes[0].node as Record<string, unknown>)
        : null
    const node = this.nodeList[0]

    if (node.uiProps != null) {
      opts.uiNodeProps = node.uiProps
    }

    if (this.loadingStateInfo) {
      this.loadingStateInfo.enabled = false
    }

    const ret = generateEditUI(
      node.fields,
      opts as never,
      data,
      this.reactObj as unknown as ReactObj,
    )

    this.editUI = ret

    ret.name = this.name

    this.validate = async (key?: string) => {
      if (key == null || key === ret.name) {
        await ret.validateForm!()
      } else {
        await ret.validateSubForm!(key)
      }
    }

    this.validateAll = async () => {
      await ret.validateForm!()

      if (ret.nodeInfo.list.length > 0) {
        Object.keys(ret.nodeInfo.map).forEach(async (key) => {
          if (ret.nodeInfo.map[key].isTable !== true) {
            await ret.validateSubForm!(key)
          }
        })
      }
    }

    this.getFields = (key?: string, exceptReadOnly?: boolean) => {
      if (key == null || key === ret.name) {
        return ret.getFormFields!(exceptReadOnly)
      }
      return ret.getSubFormFields!(key, exceptReadOnly)
    }

    this.setFields = (values: Record<string, unknown>, key?: string) => {
      if (key == null || key === ret.name) {
        return ret.setFormFields!(values)
      }
      return ret.setSubFormFields!(key, values)
    }

    ret.getTitle = (key?: string) => {
      let label = ''
      let qNode: NineNodeBase | null = null
      if (key == null) {
        qNode = node
      } else {
        qNode = node.fields![key] as NineNodeBase
      }

      const { uiProps } = qNode
      const t = (opts as { t?: (k: string) => string }).t

      if (uiProps != null) {
        label =
          (t && uiProps.labelKey
            ? /* i18next-extract-disable-next-line */
              t(uiProps.labelKey)
            : (uiProps.label as string | undefined)) || ''
      }

      return label
    }

    ret.getFormRef = (key?: string) => {
      const formKey = key == null ? this.name : key
      if (ret.refMap == null) {
        ret.refMap = {}
      }
      if (ret.refMap[formKey] == null) {
        ret.refMap[formKey] = React.createRef()
      }
      return ret.refMap[formKey]
    }

    ret.getChildNodes = (key?: string) => {
      const innerNode = key != null ? ret.nodeInfo.map[key].node : ret
      return innerNode.getNodes()
    }

    ret.getElem = (key?: string, uiAttrs?: unknown, btnsUI?: unknown) => {
      const innerNode = key != null ? ret.nodeInfo.map[key].node : ret
      const qNode = key != null ? node.fields![key] : node
      const { uiProps } = qNode as NineNodeBase

      const formRef = ret.getFormRef!(key)
      const adapter = getUIAdapter()

      return (adapter.createEditContainer as (...args: unknown[]) => React.ReactElement | null)(
        innerNode,
        options,
        uiProps,
        uiAttrs,
        formRef,
        btnsUI,
      )
    }

    this.applyEditHelpers(ret, data, this)

    return ret
  }

  // ==========================================================================
  // GQL state accessors
  // ==========================================================================

  setVariables = (vars: GQLVariables): void => {
    this.variables = vars
  }

  getVariables = (): GQLVariables => {
    return this.variables
  }

  setFixedVariables = (vars: Record<string, unknown>): void => {
    this.fixedVariables = vars
  }

  addFixedVariables = (vars: Record<string, unknown>): void => {
    this.fixedVariables = { ...this.fixedVariables, ...vars }
  }

  getFixedVariables = (): Record<string, unknown> => {
    return this.fixedVariables
  }

  setSearchItems = (items: Record<string, unknown>): void => {
    this.setUIObj({ searchItems: items })
  }

  getSearchItems = (): Record<string, unknown> | undefined => {
    const { searchItems } = this.getUIObj()
    return searchItems as Record<string, unknown> | undefined
  }

  getSearchFilters = (): Record<string, unknown> => {
    return this.searchFilters
  }

  setMobileMode = (isMobileView: boolean): void => {
    if (this.mobileMode !== isMobileView) {
      this.mobileMode = isMobileView
      const defaultV = this.getDefaultVariable()
      if (defaultV != null) {
        this.variables = { ...this.variables, ...defaultV }
      }
    }
  }

  getKeepFilters = (): FilterMap | null => {
    if (this.tableInfo != null && this.tableInfo.keepFilters != null) {
      return this.tableInfo.keepFilters as FilterMap
    }
    return null
  }

  disableParamCheck = (value: boolean): void => {
    this.isParamCheckDisabled = value
  }

  checkParams = (): unknown => {
    const pageCtrl = this.getPageControl?.()
    if (this.tableInfo != null && pageCtrl !== false) {
      if (this.isParamChecked === false) {
        let inited = true

        const defaultV = this.getDefaultVariable()
        if (defaultV != null) {
          this.variables = { ...this.variables, ...defaultV }
        }

        const sparams = getSearchParamProps()

        if (this.pageParamName != null) {
          if (sparams[this.pageParamName] != null) {
            this.setArgument(this.variables, 'pageNum', sparams[this.pageParamName])
          }
        }
        if (sparams.nfilter != null) {
          if (this.filterMap != null) {
            const nfilter: Record<string, unknown[]> = {}
            Object.entries(sparams.nfilter).forEach(([key, value]) => {
              const idxList = value as number[]
              const arr: unknown[] = []
              idxList.forEach((idx) => {
                const dataList = this.filterMap![key] as unknown[]
                arr.push(dataList[idx])
              })
              nfilter[key] = arr
            })
            this.tableInfo.defaultFilters = nfilter
            let filters: Record<string, unknown> = {}
            if (this.variables.filters != null) {
              filters = { ...(this.variables.filters as Record<string, unknown>) }

              this.filterKeyList?.forEach((key) => {
                const pos = key.lastIndexOf('.')
                const filterKey = key.substring(pos + 1)
                setInFilter(filters, filterKey, null)
              })
            }
            Object.entries(nfilter).forEach(([key, value]) => {
              const pos = key.lastIndexOf('.')
              const filterKey = key.substring(pos + 1)
              setInFilter(filters, filterKey, value as unknown[] | null | undefined)
            })
            if (Object.keys(filters).length > 0) {
              this.variables.filters = filters
            } else {
              delete this.variables.filters
            }
          } else {
            inited = false
          }
        } else {
          if (this.tableInfo.defaultFilters != null) {
            delete this.tableInfo.defaultFilters
          }
        }

        if (sparams.nsort != null) {
          if (this.sortKeyList != null) {
            const nsort: Record<string, unknown> = {}
            Object.entries(sparams.nsort).forEach(([key, value]) => {
              nsort[key] = value
            })

            this.tableInfo.defaultSorts = nsort
            let sorts: string[] = []
            if (this.variables.sort != null) {
              sorts = [...(this.variables.sort as string[])]
              this.removeSortKeys(sorts, this.sortMap)
            }

            Object.entries(nsort).forEach(([key, value]) => {
              const sortEnum = getOrderStrByName(key, value as boolean)
              sorts.push(sortEnum)
            })

            if (sorts.length > 0) {
              this.variables.sort = sorts
            } else {
              delete this.variables.sort
            }
          } else {
            inited = false
          }
        } else {
          if (this.tableInfo.defaultSorts != null) {
            delete this.tableInfo.defaultSorts
          }
        }

        const filters = { ...(this.variables.filters as Record<string, unknown>) }

        const searchItems = this.getSearchItems()

        if (searchItems != null) {
          Object.keys(searchItems).forEach((key) => {
            setFilter(filters, key, undefined)
            delete this.searchFilters[key]
          })
        }

        if (sparams.nsearch != null) {
          const { nsearch } = sparams

          Object.entries(nsearch).forEach(([key, value]) => {
            if (searchItems == null || searchItems[key] !== undefined) {
              setFilter(
                filters,
                key,
                value as string | null | undefined,
                false,
                this.searchPreset as Record<string, Array<{ key: string; value: unknown }>>,
              )
              this.searchFilters[key] = value
            }
          })
        }

        if (Object.keys(filters).length > 0) {
          this.variables.filters = filters
        } else {
          delete this.variables.filters
        }

        if (inited === true) {
          this.isParamChecked = true
        }
      }
    }
    return pageCtrl
  }

  getPaginationInfo = (): Record<string, unknown> => {
    const pageCtrl = this.checkParams()

    const { dataList } = this.getUIObj()

    return {
      current: Math.max(1, (this.getArgument(this.variables, 'pageNum') as number) || 1),
      pageSize: (this.getArgument(this.variables, 'pageSize') as number) || 5,
      onChange: (page: number) => {
        this.movePage?.(page)
      },
      total: dataList.length > 0 ? dataList[0].totalCount : 0,
      showSizeChanger: false,
      hideOnSinglePage:
        pageCtrl != null ? (pageCtrl as Record<string, unknown>).hideOnSinglePage : false,
    }
  }

  checkPageReplace = (
    _props: Record<string, unknown>,
    _prevProps: Record<string, unknown>,
  ): void => {
    // 기존 구현이 주석 처리된 상태로 남아있어 실질 동작 없음.
    // 외부 호출 호환을 위해 시그니처만 유지한다.
  }

  resetSearch = (skipRefetch: boolean, extItem: Record<string, unknown>): void => {
    const searchItems = this.getSearchItems()

    if (searchItems != null) {
      const filters = { ...(this.variables.filters as Record<string, unknown>) }

      Object.keys(searchItems).forEach((key) => {
        setFilter(filters, key, undefined)
      })

      if (extItem != null && extItem.filter != null) {
        setFilter(filters, null as unknown as string, extItem as unknown as string, true)
      }

      if (Object.keys(filters).length > 0) {
        this.variables.filters = filters
      } else {
        delete this.variables.filters
      }
    }

    this.searchFilters = {}

    if (skipRefetch !== true) {
      if (this.refetch != null) {
        this.refetch()
      } else {
        this.watch()
      }
    }
  }

  search = (
    key: string,
    value: unknown,
    searchPreset: SearchPreset,
    skipRefetch: boolean,
    useParam: boolean,
  ): void => {
    this.searchPreset = searchPreset

    const filters = { ...(this.variables.filters as Record<string, unknown>) }

    const searchItems = this.getSearchItems()

    if (searchItems == null || searchItems[key] !== undefined) {
      setFilter(
        filters,
        key,
        value as string | null | undefined,
        false,
        this.searchPreset as Record<string, Array<{ key: string; value: unknown }>>,
      )
      if (value !== undefined) {
        this.searchFilters[key] = value
      } else {
        delete this.searchFilters[key]
      }
    }

    if (useParam === true) {
      const params: Record<string, unknown> = {}
      if (this.movePage != null) {
        if (this.pageParamName != null) {
          params[this.pageParamName] = 1
          setSearchParams(params)
        }
        this.setArgument(this.variables, 'pageNum', 1)
      }
      if (Object.keys(this.searchFilters).length > 0) {
        params.nsearch = encodeURIComponent(
          encodeNSearch(this.searchFilters as Record<string, string>),
        )
      } else {
        params.nsearch = undefined
      }
      setSearchParams(params)
    }

    if (Object.keys(filters).length > 0) {
      this.variables.filters = filters
    } else {
      delete this.variables.filters
    }

    if (this.movePage != null) {
      this.movePage(1, skipRefetch, useParam)
    } else if (skipRefetch !== true) {
      this.watch()
    }
  }

  getOnChange = () => {
    return (
      pagination: Record<string, unknown>,
      filters: Record<string, unknown[]>,
      sorter: Record<string, unknown> | Record<string, unknown>[],
      extra: Record<string, unknown>,
    ) => {
      if (this.reactObj != null) {
        const onChangeFunc = this.reactObj[`onChange${this.name}`] as
          | ((...args: unknown[]) => unknown)
          | undefined
        if (onChangeFunc != null) {
          if (onChangeFunc(pagination, filters, sorter, extra, this) === true) {
            return
          }
        }
      }
      if (this.tableInfo.onChange != null) {
        if (
          (this.tableInfo.onChange as (...args: unknown[]) => unknown)(
            pagination,
            filters,
            sorter,
            extra,
            this,
          ) === true
        ) {
          return
        }
      }

      const vars = this.getVariables()

      if (extra.action === 'filter') {
        if (this.filterMap != null) {
          if (vars.filters == null) {
            vars.filters = {}
          }

          const filterStr = encodeFilterMap(
            this.filterMap as Record<string, unknown[]>,
            vars as { filters: Record<string, unknown> },
            filters,
          )

          const param = { nfilter: encodeURIComponent(filterStr) }

          setSearchParams(param)

          this.setVariables(vars)

          if (this.movePage != null) {
            this.movePage(1)
          } else {
            this.watch()
          }
        }
      } else if (extra.action === 'sort') {
        if (this.sortMap == null) {
          this.sortMap = {}
        }

        let sortList: string[] = []
        const { sortMap } = this

        if (Array.isArray(sorter) === true) {
          ;(sorter as Record<string, unknown>[]).forEach((item) => {
            processSorter(sortList, item as never, false, sortMap)
          })
        } else {
          const sorter0 = sorter as Record<string, unknown>
          const sorterColumn = sorter0.column as Record<string, unknown> | null
          if (
            sorterColumn != null &&
            sorterColumn.sorter != null &&
            (sorterColumn.sorter as Record<string, unknown>).multiple != null
          ) {
            processSorter(sortList, sorter0 as never, false, sortMap)
          } else {
            if (vars.sort != null) {
              sortList = [...(vars.sort as string[])]
              this.removeSortKeys(sortList, sortMap)
            }
            processSorter(sortList, sorter0 as never, true, sortMap)
          }
        }

        this.tableInfo.defaultSorts = { ...sortMap }

        const sortStr = encodeSortMap(sortMap)

        const param: { nsort: string | undefined } = {
          nsort: encodeURIComponent(sortStr),
        }

        if (param.nsort.length === 0) {
          param.nsort = undefined
        }

        setSearchParams(param)

        if (sortList.length > 0) {
          vars.sort = sortList
        } else {
          vars.sort = null
        }
        this.setVariables(vars)

        if (this.refetch != null) {
          this.refetch()
        } else {
          this.watch()
        }
      } else if (extra.action === 'paginate') {
        this.movePage?.(pagination.current as number)
      }
    }
  }

  // ==========================================================================
  // Rendering helper — PaginatedResultObjBase 의 getTable*/getList*/getArrayElem 이 호출.
  // 쿼리 결과의 `dataList[0].nodes` 를 dataSource 로 사용.
  // ==========================================================================

  protected getElemData(
    options: Record<string, unknown> | null | undefined,
    elemInfo: unknown,
    itemFunc: (...args: unknown[]) => unknown,
    retFunc: (...args: unknown[]) => unknown,
  ): unknown {
    if (this.nodeList == null || this.nodeList.length === 0) {
      throw new Error('Field must not be empty.')
    }
    const node = this.nodeList[0]

    const { dataList } = this.getUIObj()

    let dataSource = dataList.length > 0 ? dataList[0].nodes : []
    if (options != null && options.getDataSource != null) {
      dataSource = (options.getDataSource as (d: unknown) => GQLResponseNode[])(dataSource)
    }

    const itemInfo = itemFunc(node, options, elemInfo, this)

    return retFunc(node, options, elemInfo, itemInfo, dataSource, this)
  }

  setAllDataList = (dataList: NodeDataSet[], loading?: boolean): void => {
    if (this.editUI != null) {
      const formRef = this.editUI.getFormRef!(this.name) as {
        current: FormInstance | null
      } | null
      const nodes = dataList[0].nodes as Array<Record<string, unknown>>
      if (formRef != null && formRef.current != null) {
        if (nodes.length > 0) {
          formRef.current.setFieldsValue(
            this.editUI.initFields!(nodes[0].node as Record<string, unknown>),
          )
        }
      }
      const { nodeInfo } = this.editUI
      if (nodeInfo.list.length > 0) {
        Object.keys(nodeInfo.map).forEach((key) => {
          const childNode = nodeInfo.map[key]
          if (childNode.isTable !== true) {
            const subFormRef = this.editUI!.getFormRef!(key) as {
              current: FormInstance | null
            } | null
            if (subFormRef != null && subFormRef.current != null && nodes.length > 0) {
              subFormRef.current.setFieldsValue(
                (childNode.node as EditFormHandler).initFields!(
                  (nodes[0].node as Record<string, unknown>)[key] as Record<string, unknown>,
                ),
              )
            }
          }
        })
      }
      return
    }
    if (this.reactObj != null) {
      this.setLoading(loading, false)
      this.setUIObj({ dataList })
    }
  }

  private copyInnerValue = (
    target: Record<string, unknown>,
    props: Record<string, unknown>,
  ): void => {
    Object.entries(props).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        if (target[key] == null) {
          target[key] = {}
        }
        this.copyInnerValue(
          target[key] as Record<string, unknown>,
          value as Record<string, unknown>,
        )
      } else {
        target[key] = value
      }
    })
  }

  // filter는 page이동 또는 parameter 변경 시 자동으로 삭제되는 속성이므로
  // 필요한 경우, fixedVaraible 또는 keepFilter를 이용하여 값을 유지시켜야 함.
  private getFinalVariables = (v: GQLVariables): GQLVariables => {
    const ret: GQLVariables = { ...v }
    this.copyInnerValue(ret, this.fixedVariables)
    return ret
  }

  // ==========================================================================
  // Apollo operations
  // ==========================================================================

  watch = (
    cbFunc?: (a: GQLResponseData, b?: unknown) => void,
    prop?: Record<string, unknown>,
    otherClient?: GQLApolloClient,
  ): ObservableQuery<unknown> => {
    const options = prop != null ? prop : {}

    // 재구독 전 기존 구독을 해제한다. close() 없이 동일 인스턴스에서 watch() 를 재호출하면
    // 이전 Apollo 구독이 unsubscribe 되지 않은 채 덮어써져 ObservableQuery 가 누수된다.
    if (this.subscription != null) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    const targetClient = (otherClient || ngqlInfo.defaultClient) as GQLApolloClient

    this.checkParams()

    const finalVars = this.getFinalVariables(this.variables)

    const observableQuery = targetClient.watchQuery({
      query: gql(this.queryText),
      variables: finalVars,
      fetchPolicy: 'network-only',
      ...options,
    })

    this.setLoading(true)

    const subscription = observableQuery.subscribe({
      next: (res) => {
        let { dataList } = this.getUIObj()

        if (this.useUI === true) {
          getMappingRes(res, this)

          const { dataList: newDataList } = this.getUIObj()
          dataList = newDataList

          if (cbFunc != null) {
            cbFunc(res as GQLResponseData, dataList)
          }

          const pageCtrl = this.getPageControl?.()

          if (this.tableInfo != null && dataList.length > 0 && pageCtrl !== false) {
            const defaultV = this.getDefaultVariable()
            const pageSize = this.getArgument(defaultV, 'pageSize', 5) as number

            const totalPage = getTotalPageCnt(dataList[0].totalCount, pageSize)
            if (totalPage > 0 && (this.getArgument(defaultV, 'pageNum') as number) > totalPage) {
              setTimeout(() => {
                this.movePage(totalPage)
              }, 10)
            }
          }
        } else if (cbFunc != null) {
          cbFunc(res as GQLResponseData)
        }

        if (this.useUI === true) {
          this.setAllDataList(dataList, (res as GQLResponseData).loading)
        }
      },
      error: () => {
        this.setLoading(false)
      },
      complete: () => {
        this.setLoading(false)
      },
    })

    this.observableQuery = observableQuery as unknown as ObservableQuery<unknown>
    this.subscription = subscription as unknown as { unsubscribe: () => void }
    this.refetchPrevVariables = null

    addParamChangeListener(this.resetParamCheck)

    return observableQuery as unknown as ObservableQuery<unknown>
  }

  refetch = (vars?: GQLVariables): Promise<unknown> | null => {
    if (this.observableQuery == null) {
      return null
    }
    const v = this.getFinalVariables(vars || this.variables)

    const apolloVariables = (this.observableQuery as unknown as { variables: GQLVariables })
      .variables
    if (this.refetchPrevVariables != null) {
      Object.entries(this.refetchPrevVariables).forEach(([key, value]) => {
        if (value != null && v[key] == null) {
          delete apolloVariables[key]
        }
      })
    }

    this.refetchPrevVariables = { ...v }

    if (this.reactObj != null && this.uiInfo != null && this.uiInfo.loadingStateName != null) {
      const useLoadingState = this.isLoadingStateEnabled()

      let timer: ReturnType<typeof setTimeout> | null = null
      if (useLoadingState !== false && this.reactObj != null) {
        timer = setTimeout(() => {
          this.setLoading(true)
        }, 500)
      }
      this.observableQuery.refetch(v).finally(() => {
        if (timer != null) {
          clearTimeout(timer)
        }
        this.setLoading(false)
      })
      return null
    }

    return this.observableQuery.refetch(v)
  }

  resetCache = (): void => {
    if (this.observableQuery != null) {
      ;(this.observableQuery as unknown as { resetLastResults?: () => void }).resetLastResults?.()
    } else if (this.observable != null) {
      this.observable.resetLastResults?.()
    }
  }

  movePage = (page: number, skipRefetch?: boolean, useParams?: boolean): void => {
    if (skipRefetch !== true || useParams !== false) {
      this.needDataClear = true
      this.setArgument(this.variables, 'pageNum', page)
      if (this.variables.after != null) {
        delete this.variables.after
      }
    }

    if (skipRefetch !== true) {
      this.refetch(this.variables)
    }

    if (useParams !== false) {
      const obj: Record<string, unknown> = {}
      if (this.pageParamName != null) {
        obj[this.pageParamName] = page
        setSearchParams(obj)
      }
    }
  }

  morePage = (remainData: unknown): void => {
    const { dataList } = this.getUIObj()
    this.needDataClear = remainData != null ? !remainData : false
    this.setArgument(this.variables, 'pageNum', 1)

    if (dataList.length > 0) {
      const curData = dataList[0]
      const { nodes } = curData
      const lastData = nodes[nodes.length - 1]
      if (this.arrayIFace != null) {
        const cursor = this.arrayIFace.getMoreCursor!(null, lastData)
        this.setArgument(this.variables, 'after', cursor)
      } else if (lastData.cursor != null) {
        this.variables.after = lastData.cursor
      } else {
        throw new Error('cursor is required.')
      }
    }

    setSearchParams({}, false)

    this.refetch(this.variables)
  }

  resetParamCheck = (): void => {
    if (this.isParamCheckDisabled === true) {
      return
    }

    this.isParamChecked = false
    const defaultV = this.getDefaultVariable()
    const keepFilters = this.getKeepFilters()

    let hasKeepFilterData = false
    const filters: Record<string, unknown> = {}
    if (keepFilters != null && this.variables != null && this.variables.filters != null) {
      const currentFilters = this.variables.filters as Record<string, unknown>
      for (const item of Object.keys(keepFilters)) {
        if (keepFilters[item] === true && currentFilters[item] !== undefined) {
          filters[item] = currentFilters[item]
          hasKeepFilterData = true
        }
      }
    }

    if (defaultV != null) {
      this.variables = { ...defaultV }
    } else {
      this.variables = {}
    }

    if (hasKeepFilterData === true) {
      if (this.variables.filters != null) {
        this.variables.filters = {
          ...(this.variables.filters as Record<string, unknown>),
          ...filters,
        }
      } else {
        this.variables.filters = filters
      }
    }

    this.checkParams()

    if (this.observableQuery != null) {
      this.refetch()
    } else {
      this.watch()
    }
  }

  close = (): void => {
    removeParamChangeListener(this.resetParamCheck)

    if (this.subscription != null) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    this.cleanupMobileAndFilters()
  }

  query = (
    prop?: Record<string, unknown>,
    otherClient?: GQLApolloClient,
  ): Promise<GQLResponseData> => {
    const options = prop != null ? prop : {}

    return new Promise<GQLResponseData>((resolve, reject) => {
      const targetClient = (otherClient || ngqlInfo.defaultClient) as GQLApolloClient

      this.setLoading(true)

      const finalVars = this.getFinalVariables(this.variables)

      targetClient
        .query({
          query: gql(this.queryText),
          variables: finalVars,
          fetchPolicy: 'network-only',
          ...options,
        })
        .then((res) => {
          if (this.useUI === true) {
            getMappingRes(res, this)
            const { dataList } = this.getUIObj()
            this.setAllDataList(dataList, false)
          }
          resolve(res as GQLResponseData)
        })
        .catch((error) => {
          this.setLoading(false)
          const err = getFirstError(error as GraphQLErrorLike)
          console.log(' ---- ', err.extensions, err.message)

          reject(err)
        })
    })
  }

  mutate = (
    prop?: Record<string, unknown>,
    otherClient?: GQLApolloClient,
  ): Promise<GQLResponseData> => {
    const options = prop != null ? prop : {}

    return new Promise<GQLResponseData>((resolve, reject) => {
      const targetClient = (otherClient || ngqlInfo.defaultClient) as GQLApolloClient

      const finalVars = this.getFinalVariables(this.variables)

      targetClient
        .mutate({
          mutation: gql(this.queryText),
          variables: finalVars,
          ...options,
        })
        .then((res) => {
          if (this.useUI === true) {
            getMappingMutationRes(res, this)
            const { dataList } = this.getUIObj()
            this.setAllDataList(dataList, false)
          }
          resolve(res as GQLResponseData)
        })
        .catch((error) => {
          const err = getFirstError(error as GraphQLErrorLike)
          console.log(' ---- ', err.extensions, err.message)

          reject(err)
        })
    })
  }

  subscribe = (
    cbFunc?: (data: GQLResponseData, extra?: unknown) => void,
    prop?: Record<string, unknown>,
    otherClient?: GQLApolloClient,
  ): { unsubscribe: () => void } => {
    const options = prop != null ? prop : {}

    // 재구독 전 기존 구독을 해제한다(watch()/subscribe() 공용 this.subscription).
    // close() 없이 재호출 시 이전 구독이 덮어써지며 누수되는 것을 방지한다.
    if (this.subscription != null) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    const targetClient = (otherClient || ngqlInfo.defaultClient) as GQLApolloClient

    const finalVars = this.getFinalVariables(this.variables)

    const observable = targetClient.subscribe({
      query: gql(this.queryText),
      variables: finalVars,
      fetchPolicy: 'network-only',
      ...options,
    })

    const subscription = observable.subscribe({
      next: (res) => {
        const resData = res as GQLResponseData
        let { dataList } = this.getUIObj()

        if (this.useUI === true) {
          getMappingRes(resData, this)

          const { dataList: newDataList } = this.getUIObj()
          dataList = newDataList

          if (cbFunc != null) {
            cbFunc(resData, dataList)
          }
        } else if (cbFunc != null) {
          cbFunc(resData)
        }

        if (this.useUI === true) {
          this.setAllDataList(dataList, (res as Record<string, unknown>).loading as boolean)
        }
      },
      error: () => {
        console.log('- observable error')
        this.setLoading(false)
      },
      complete: () => {
        this.setLoading(false)
      },
    })

    this.observable = observable as unknown as ResultObj['observable']
    this.subscription = subscription as unknown as { unsubscribe: () => void }

    return observable as unknown as { unsubscribe: () => void }
  }
}

// ============================================================================
// Response mapping helpers
// ============================================================================

export function getMappingRes(
  res: { data?: Record<string, unknown> | null },
  resultObj: ResultObj,
): { response: unknown; resultObj: ResultObj } {
  const resRoot = res.data || {}
  resultObj.mappingResponse(resRoot)
  return { response: res, resultObj }
}

export function getMappingMutationRes(
  res: { data?: Record<string, unknown> | null },
  resultObj: ResultObj,
): { response: unknown; resultObj: ResultObj } {
  const resRoot = res.data || {}
  resultObj.mappingMutationResponse(resRoot)
  return { response: res, resultObj }
}

// ============================================================================
// GQLObj 팩토리
// ============================================================================

export function GQLObj(
  queryObj: NineOperation,
  gqlopt: GQLObjOptions,
  reactObj?: React.Component,
): ResultObj {
  return new ResultObj(queryObj as NineNodeBase, gqlopt, reactObj as unknown as ReactObj)
}
